import React, { useEffect, useState, useMemo } from "react"
import { Modal } from "reactstrap"
import api from "API/Api"
import SweetAlert from "react-bootstrap-sweetalert"
import PageLoader from "./../../assets/loading/pageLoad1.gif"
import { usePermissions } from "API/PermissionContext"

const STATUS_MAP = { 10: "Pending", 20: "Loading", 30: "In Transit", 40: "Unloading", 50: "Completed", 60: "Cancelled" }
const STATUS_COLOR = { 10: "warning", 20: "info", 30: "primary", 40: "secondary", 50: "success", 60: "danger" }

const MilkTransfers = () => {
  const { canAdd, canEdit, canDelete, MODULE } = usePermissions()
  const MID = MODULE.MILK_TRANSFERS

  // list state
  const [records, setRecords] = useState([])
  const [pageLoad, setPageLoad] = useState(true)
  const [searchTxt, setSearchTxt] = useState("")
  const [page, setPage] = useState(1)
  const [totalPages, setTotalPages] = useState(1)
  const [selected, setSelected] = useState([])
  const [showFilter, setShowFilter] = useState(false)
  const [fDate, setFDate] = useState("")
  const [fDateTo, setFDateTo] = useState("")

  // delete
  const [confirm_alert, setconfirm_alert] = useState(false)
  const [success_dlg, setsuccess_dlg] = useState(false)
  const [dlgMsg, setDlgMsg] = useState("")
  const [deleteId, setDeleteId] = useState("")

  // form data (dropdowns)
  const [companies, setCompanies] = useState([])
  const [plants, setPlants] = useState([])
  const [ccs, setCcs] = useState([])
  const [vehicles, setVehicles] = useState([])
  const [businessVendors, setBusinessVendors] = useState([])
  const [staffs, setStaffs] = useState([])

  // modal
  const [modalOpen, setModalOpen] = useState(false)
  const [editId, setEditId] = useState(null)
  const [saving, setSaving] = useState(false)

  // form fields
  const [transferType, setTransferType] = useState("internal")
  const [businessVendorId, setBusinessVendorId] = useState("")
  const [transferDate, setTransferDate] = useState("")
  const [vehicleId, setVehicleId] = useState("")
  const [driverName, setDriverName] = useState("")
  const [driverMobile, setDriverMobile] = useState("")
  const [locationType, setLocationType] = useState("plant")
  const [loadingLocationId, setLoadingLocationId] = useState("")
  const [plantId, setPlantId] = useState("")
  const [loadingInTime, setLoadingInTime] = useState("")
  const [loadingOutTime, setLoadingOutTime] = useState("")
  const [sealNumber, setSealNumber] = useState("")
  const [sealImage, setSealImage] = useState(null)
  const [approvedBy, setApprovedBy] = useState("")
  const [remarks, setRemarks] = useState("")

  useEffect(() => { loadData(1) }, [])

  const loadData = async (p) => {
    setPageLoad(true)
    try {
      const resp = await api.get("milk-transfers?per_page=200&page=" + (p || 1))
      const d = resp.data?.data
      setRecords(d?.data || [])
      setPage(d?.current_page || 1)
      setTotalPages(d?.last_page || 1)
    } catch (e) { setRecords([]) }
    setPageLoad(false)
  }

  const loadFormData = async () => {
    try {
      const resp = await api.get("milk-transfer-form-data")
      const d = resp.data?.data
      if (d) {
        setCompanies(d.companies || [])
        setPlants(d.plants || [])
        setCcs(d.chillingCenters || [])
        setVehicles(d.vehicles || [])
        setBusinessVendors(d.businessVendors || [])
        setStaffs(d.staffs || [])
      }
    } catch (e) { console.error("Form data error", e) }
  }

  // filtered records
  const filtered = useMemo(() => {
    let d = records
    if (searchTxt) {
      const s = searchTxt.toLowerCase()
      d = d.filter(r =>
        (r.companyName || "").toLowerCase().includes(s) ||
        (r.vehicleNumber || "").toLowerCase().includes(s) ||
        (r.driverName || "").toLowerCase().includes(s) ||
        (r.transferDate || "").includes(s)
      )
    }
    if (fDate) d = d.filter(r => r.transferDate >= fDate)
    if (fDateTo) d = d.filter(r => r.transferDate <= fDateTo)
    return d
  }, [records, searchTxt, fDate, fDateTo])

  // selection
  const allSelected = filtered.length > 0 && filtered.every(r => selected.includes(r.id))
  const toggleAll = () => allSelected ? setSelected([]) : setSelected(filtered.map(r => r.id))
  const toggleOne = id => setSelected(prev => prev.includes(id) ? prev.filter(x => x !== id) : [...prev, id])

  // helper: format datetime for input
  const nowDateTime = () => {
    const d = new Date()
    const pad = n => String(n).padStart(2, "0")
    return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}T${pad(d.getHours())}:${pad(d.getMinutes())}`
  }

  // open add modal
  const openAdd = async () => {
    await loadFormData()
    setEditId(null)
    setTransferType("internal")
    setBusinessVendorId("")
    setTransferDate("")
    setVehicleId("")
    setDriverName("")
    setDriverMobile("")
    setLocationType("plant")
    setLoadingLocationId("")
    setPlantId("")
    setLoadingInTime(nowDateTime())
    setLoadingOutTime(nowDateTime())
    setSealNumber("")
    setSealImage(null)
    setApprovedBy("")
    setRemarks("")
    setModalOpen(true)
  }

  // open edit modal
  const openEdit = async (id) => {
    await loadFormData()
    try {
      const resp = await api.get("milk-transfer/" + id)
      const r = resp.data?.data
      if (!r) return alert("Not found")
      setEditId(id)
      setTransferType(r.transferType || "internal")
      setBusinessVendorId(r.business_vendor_id || "")
      setTransferDate(r.transferDate || "")
      setVehicleId(r.vehicle_id || "")
      setDriverName(r.driverName || "")
      setDriverMobile(r.driverMobile || "")
      setLocationType(r.locationType || "plant")
      setLoadingLocationId(r.loading_location_id || "")
      setPlantId(r.plant_id || "")
      setLoadingInTime(r.loadingInTime || "")
      setLoadingOutTime(r.loadingOutTime || "")
      setSealNumber(r.sealNumber || "")
      setSealImage(null)
      setApprovedBy(r.approvedBy || "")
      setRemarks(r.remarks || "")
      setModalOpen(true)
    } catch (e) { alert("Error loading record") }
  }

  // vehicle change -> auto-fill driver
  const onVehicleChange = (vId) => {
    setVehicleId(vId)
    const v = vehicles.find(x => String(x.id) === String(vId))
    if (v) {
      setDriverName(v.driverName || "")
      setDriverMobile(v.driverMobile || "")
    }
  }

  // get location options based on type
  const locationOptions = useMemo(() => {
    if (locationType === "chilling_center") return ccs
    return plants
  }, [locationType, ccs, plants])

  // save
  const handleSave = async () => {
    if (!transferDate) return alert("Date is required")
    if (!vehicleId) return alert("Vehicle is required")
    if (!driverName) return alert("Driver Name is required")
    if (transferType === "outside" && !businessVendorId) return alert("Business Vendor is required for Outside Sales")

    setSaving(true)
    try {
      const fd = new FormData()
      fd.append("transferType", transferType)
      fd.append("transferDate", transferDate)
      fd.append("vehicle_id", vehicleId)
      const v = vehicles.find(x => String(x.id) === String(vehicleId))
      fd.append("vehicleNumber", v?.vehicleNumber || "")
      fd.append("driverName", driverName)
      fd.append("driverMobile", driverMobile || "")
      fd.append("locationType", locationType)
      fd.append("loading_location_id", loadingLocationId || "")
      fd.append("plant_id", plantId || "")
      fd.append("loadingInTime", loadingInTime || "")
      fd.append("loadingOutTime", loadingOutTime || "")
      fd.append("sealNumber", sealNumber || "")
      fd.append("approvedBy", approvedBy || "")
      fd.append("remarks", remarks || "")
      if (transferType === "outside") fd.append("business_vendor_id", businessVendorId)
      if (locationType === "chilling_center") {
        fd.append("chilling_center_id", loadingLocationId || "")
        const cc = ccs.find(x => String(x.id) === String(loadingLocationId))
        fd.append("loading_location_name", cc?.name || "")
      } else {
        fd.append("plant_id", loadingLocationId || "")
        const p = plants.find(x => String(x.id) === String(loadingLocationId))
        fd.append("loading_location_name", p?.name || "")
      }
      if (sealImage) fd.append("sealImage", sealImage)

      if (editId) {
        await api.post("update-milk-transfer/" + editId, fd, { headers: { "Content-Type": "multipart/form-data" } })
        setDlgMsg("Updated successfully")
      } else {
        await api.post("create-milk-transfer", fd, { headers: { "Content-Type": "multipart/form-data" } })
        setDlgMsg("Created successfully")
      }
      setModalOpen(false)
      setsuccess_dlg(true)
      loadData(1)
    } catch (e) { alert("Error: " + (e.response?.data?.data || e.message)) }
    setSaving(false)
  }

  // delete
  const handleDelete = async () => {
    try {
      if (deleteId) {
        await api.delete("delete-milk-transfer/" + deleteId)
      } else if (selected.length) {
        await api.post("bulk-delete-milk-transfers", { ids: selected })
        setSelected([])
      }
      setDlgMsg("Deleted successfully")
      setsuccess_dlg(true)
      loadData(page)
    } catch (e) { alert("Delete failed") }
    setDeleteId("")
  }

  // pager
  const Pager = ({ cur, total, onChange }) => (
    <div style={{ display: "flex", justifyContent: "flex-end", alignItems: "center", gap: 4, marginTop: 8 }}>
      <button className="btn btn-sm btn-outline-secondary" onClick={() => onChange(1)} disabled={cur <= 1}>--</button>
      <button className="btn btn-sm btn-outline-secondary" onClick={() => onChange(cur - 1)} disabled={cur <= 1}>&lt;</button>
      <span style={{ padding: "2px 10px", background: "#0d6efd", color: "#fff", borderRadius: 4, fontSize: 13 }}>{cur}</span>
      <button className="btn btn-sm btn-outline-secondary" onClick={() => onChange(cur + 1)} disabled={cur >= total}>&gt;</button>
      <button className="btn btn-sm btn-outline-secondary" onClick={() => onChange(total)} disabled={cur >= total}>--</button>
    </div>
  )

  const ib = (bg, icon, title, onClick, disabled) => (
    <button title={title} onClick={onClick} disabled={disabled}
      style={{ width: 32, height: 32, borderRadius: 4, background: disabled ? "#ccc" : bg, color: "#fff", border: "none", display: "inline-flex", alignItems: "center", justifyContent: "center", fontSize: 14, cursor: disabled ? "not-allowed" : "pointer" }}>
      <i className={`fas ${icon}`} />
    </button>
  )

  return (
    <>
      {/* Toolbar: Title + Icons + Search in one row */}
      <div style={{
        display: "flex", alignItems: "center", flexWrap: "wrap", gap: "6px",
        marginBottom: "10px", background: "#f8f9fa",
        borderRadius: "8px", padding: "4px 8px", border: "1px solid #dee2e6",
      }}>
        <h5 style={{ fontWeight: 600, color: "#2563eb", margin: 0, marginRight: 10, fontSize: 16, whiteSpace: "nowrap" }}>Milk Transfers</h5>
        <div style={{ width: 1, height: 24, background: "#dee2e6", margin: "0 2px" }} />
        {canDelete(MID) && ib("#dc3545", "fa-trash", selected.length > 0 ? `Delete ${selected.length}` : "Delete Selected", () => { if (selected.length) setconfirm_alert(true); else alert("Select records first") }, !selected.length)}
        {canAdd(MID) && ib("#28a745", "fa-plus", "Add Milk Transfer", openAdd, false)}
        {ib("#0d6efd", "fa-filter", "Toggle Filter", () => { setShowFilter(!showFilter); if (showFilter) { setFDate(""); setFDateTo("") } }, false)}

        <div style={{ flex: 1 }} />

        <div style={{ display: "flex", gap: 4 }}>
          <input className="form-control form-control-sm" style={{ width: 180, fontSize: 13 }} placeholder="Search" value={searchTxt} onChange={e => setSearchTxt(e.target.value)} />
          <button className="btn btn-sm btn-primary" style={{ padding: "2px 8px" }}><i className="fas fa-search" /></button>
        </div>
      </div>

      {/* Content */}
      {pageLoad ? <div className="pageLoader"><img src={PageLoader} alt="Loading..." /></div> : (
        <div style={{ overflowX: "auto" }}>
          <table className="table table-sm table-bordered table-hover mb-0 dairyTbl" style={{ fontSize: 12 }}>
            <thead style={{ background: "#f8f9fa" }}>
              <tr>
                <th style={{ width: 28 }}><input type="checkbox" checked={allSelected} onChange={toggleAll} /></th>
                <th>Company</th><th>Date</th><th>Vehicle</th><th>Driver</th><th>Status</th><th>Action</th>
              </tr>
              {showFilter && (
                <tr style={{ background: "#f1f3f5" }}>
                  <td></td><td></td>
                  <td>
                    <input type="date" className="form-control form-control-sm" value={fDate} onChange={e => setFDate(e.target.value)} />
                    <input type="date" className="form-control form-control-sm mt-1" value={fDateTo} onChange={e => setFDateTo(e.target.value)} />
                  </td>
                  <td colSpan={3}></td>
                  <td><button className="btn btn-sm btn-outline-danger" onClick={() => { setFDate(""); setFDateTo("") }}><i className="fa fa-times" /></button></td>
                </tr>
              )}
            </thead>
            <tbody>
              {filtered.length > 0 ? filtered.map((r, i) => (
                <tr key={r.id}>
                  <td><input type="checkbox" checked={selected.includes(r.id)} onChange={() => toggleOne(r.id)} /></td>
                  <td>{r.companyName || "-"}</td>
                  <td style={{ whiteSpace: "nowrap", fontSize: 11 }}>{r.transferDate}</td>
                  <td>{r.vehicleNumber || "-"}</td>
                  <td>{r.driverName || "-"}</td>
                  <td><span className={`badge bg-${STATUS_COLOR[r.status] || "secondary"}`}>{STATUS_MAP[r.status] || r.status}</span></td>
                  <td>
                    <div style={{ display: "flex", gap: "4px" }}>
                      {canEdit(MID) && <button style={{ width: "28px", height: "28px", borderRadius: "4px", background: "#0d6efd", color: "#fff", border: "none", display: "inline-flex", alignItems: "center", justifyContent: "center", fontSize: "12px", cursor: "pointer" }} onClick={() => openEdit(r.id)}><i className="fas fa-edit"></i></button>}
                    </div>
                  </td>
                </tr>
              )) : (
                <tr><td colSpan={7}><center style={{ padding: 20, color: "#6c757d" }}>No matching records found</center></td></tr>
              )}
            </tbody>
          </table>
          <Pager cur={page} total={totalPages} onChange={p => loadData(p)} />
        </div>
      )}

      {/* Add/Edit Modal */}
      <Modal isOpen={modalOpen} toggle={() => setModalOpen(false)} size="xl" backdrop="static">
        <div style={{ padding: "20px 24px" }}>
          <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 16 }}>
            <h5 style={{ margin: 0 }}>Milk Transfer Loadings <span style={{ color: "red", fontSize: 13 }}>[* required]</span></h5>
            <button type="button" className="btn-close" onClick={() => setModalOpen(false)} />
          </div>
          <h6 style={{ fontWeight: 600, marginBottom: 16, color: "#495057" }}>Milk Transfer Loadings Information</h6>

          <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr 1fr 1fr", gap: "12px 16px" }}>
            {/* Row 1 */}
            <div>
              <label className="form-label" style={{ fontWeight: 700, fontSize: 13 }}>Milk Transfer Type *</label>
              <div style={{ display: "flex", gap: 16 }}>
                <label style={{ fontSize: 13 }}><input type="radio" checked={transferType === "internal"} onChange={() => setTransferType("internal")} /> Internal Transfer</label>
                <label style={{ fontSize: 13 }}><input type="radio" checked={transferType === "outside"} onChange={() => setTransferType("outside")} /> Outside Sales</label>
              </div>
            </div>

            {transferType === "outside" ? (
              <>
                <div>
                  <label className="form-label" style={{ fontWeight: 700, fontSize: 13 }}>Business Vendor *</label>
                  <select className="form-control form-control-sm" value={businessVendorId} onChange={e => setBusinessVendorId(e.target.value)}>
                    <option value="">Select</option>
                    {businessVendors.map(bv => <option key={bv.id} value={bv.id}>{bv.name}</option>)}
                  </select>
                </div>
                <div>
                  <label className="form-label" style={{ fontWeight: 700, fontSize: 13 }}>Date *</label>
                  <input type="date" className="form-control form-control-sm" value={transferDate} onChange={e => setTransferDate(e.target.value)} />
                </div>
                <div>
                  <label className="form-label" style={{ fontWeight: 700, fontSize: 13 }}>Vehicle *</label>
                  <select className="form-control form-control-sm" value={vehicleId} onChange={e => onVehicleChange(e.target.value)}>
                    <option value="">Select</option>
                    {vehicles.map(v => <option key={v.id} value={v.id}>{v.vehicleNumber}</option>)}
                  </select>
                </div>
              </>
            ) : (
              <>
                <div>
                  <label className="form-label" style={{ fontWeight: 700, fontSize: 13 }}>Date *</label>
                  <input type="date" className="form-control form-control-sm" value={transferDate} onChange={e => setTransferDate(e.target.value)} />
                </div>
                <div>
                  <label className="form-label" style={{ fontWeight: 700, fontSize: 13 }}>Vehicle *</label>
                  <select className="form-control form-control-sm" value={vehicleId} onChange={e => onVehicleChange(e.target.value)}>
                    <option value="">Select</option>
                    {vehicles.map(v => <option key={v.id} value={v.id}>{v.vehicleNumber}</option>)}
                  </select>
                </div>
                <div>
                  <label className="form-label" style={{ fontWeight: 700, fontSize: 13 }}>Driver Name *</label>
                  <input className="form-control form-control-sm" value={driverName} onChange={e => setDriverName(e.target.value)} />
                </div>
              </>
            )}

            {/* Row 2 */}
            {transferType === "outside" && (
              <div>
                <label className="form-label" style={{ fontWeight: 700, fontSize: 13 }}>Driver Name *</label>
                <input className="form-control form-control-sm" value={driverName} onChange={e => setDriverName(e.target.value)} />
              </div>
            )}
            <div>
              <label className="form-label" style={{ fontWeight: 700, fontSize: 13 }}>Driver Mobile *</label>
              <input className="form-control form-control-sm" value={driverMobile} onChange={e => setDriverMobile(e.target.value)} />
            </div>
            <div>
              <label className="form-label" style={{ fontWeight: 700, fontSize: 13 }}>Loading Location Type *</label>
              <div style={{ display: "flex", gap: 16 }}>
                <label style={{ fontSize: 13 }}><input type="radio" checked={locationType === "chilling_center"} onChange={() => { setLocationType("chilling_center"); setLoadingLocationId("") }} /> Chilling Center</label>
                <label style={{ fontSize: 13 }}><input type="radio" checked={locationType === "plant"} onChange={() => { setLocationType("plant"); setLoadingLocationId("") }} /> Plant</label>
              </div>
            </div>
            <div>
              <label className="form-label" style={{ fontWeight: 700, fontSize: 13 }}>Loading {locationType === "plant" ? "Plant" : "CC"} *</label>
              <select className="form-control form-control-sm" value={loadingLocationId} onChange={e => setLoadingLocationId(e.target.value)}>
                <option value="">Select</option>
                {locationOptions.map(o => <option key={o.id} value={o.id}>{o.name}</option>)}
              </select>
            </div>

            {/* Row 3 */}
            <div>
              <label className="form-label" style={{ fontWeight: 700, fontSize: 13 }}>Vehicle In Time *</label>
              <input type="datetime-local" className="form-control form-control-sm" value={loadingInTime} onChange={e => setLoadingInTime(e.target.value)} />
            </div>
            <div>
              <label className="form-label" style={{ fontWeight: 700, fontSize: 13 }}>Seal Number</label>
              <input className="form-control form-control-sm" value={sealNumber} onChange={e => setSealNumber(e.target.value)} />
            </div>
            <div>
              <label className="form-label" style={{ fontWeight: 700, fontSize: 13 }}>Seal Image</label>
              <input type="file" className="form-control form-control-sm" accept="image/*" onChange={e => setSealImage(e.target.files[0] || null)} />
            </div>
            <div>
              <label className="form-label" style={{ fontWeight: 700, fontSize: 13 }}>Approved By *</label>
              <select className="form-control form-control-sm" value={approvedBy} onChange={e => setApprovedBy(e.target.value)}>
                <option value="">Select</option>
                {staffs.map(s => <option key={s.id} value={s.id}>{s.staffName}</option>)}
              </select>
            </div>

            {/* Row 4 */}
            <div>
              <label className="form-label" style={{ fontWeight: 700, fontSize: 13 }}>Remarks</label>
              <input className="form-control form-control-sm" value={remarks} onChange={e => setRemarks(e.target.value)} />
            </div>
            <div>
              <label className="form-label" style={{ fontWeight: 700, fontSize: 13 }}>Vehicle Out Time *</label>
              <input type="datetime-local" className="form-control form-control-sm" value={loadingOutTime} onChange={e => setLoadingOutTime(e.target.value)} />
            </div>
          </div>

          {/* Footer */}
          <div style={{ display: "flex", justifyContent: "flex-end", gap: 8, marginTop: 20 }}>
            <button className="btn btn-success btn-sm" style={{ fontWeight: 700 }} onClick={handleSave} disabled={saving}>{saving ? "Saving..." : "Save Changes"}</button>
            <button className="btn btn-danger btn-sm" onClick={() => setModalOpen(false)}>Close</button>
          </div>
        </div>
      </Modal>

      {/* Alerts */}
      {confirm_alert && <SweetAlert title="Are you sure?" warning showCancel confirmBtnBsStyle="success" cancelBtnBsStyle="danger"
        onConfirm={() => { handleDelete(); setconfirm_alert(false) }}
        onCancel={() => { setconfirm_alert(false); setDeleteId("") }}>You won't be able to revert this!</SweetAlert>}
      {success_dlg && <SweetAlert success title="Done!" onConfirm={() => { setsuccess_dlg(false); setDeleteId(""); setDlgMsg("") }}>{dlgMsg || "Operation completed."}</SweetAlert>}
    </>
  )
}

export default MilkTransfers
